import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import apiClient from '../api/client';
import { useStore, useCustomers } from '../store/useStore';
import type { Customer, CreateCustomerRequest, UpdateCustomerRequest, ImportedKey } from '../store/customer-slice';

function RoleBadge({ role }: { role: Customer['role'] }) {
  const styles: Record<Customer['role'], string> = {
    admin: 'badge-warning',
    customer: 'badge-info',
  };

  return <span className={clsx('badge', styles[role])}>{role}</span>;
}

function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span className={clsx('badge', isActive ? 'badge-success' : 'badge-error')}>
      {isActive ? 'active' : 'inactive'}
    </span>
  );
}

interface CreateCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (request: CreateCustomerRequest) => Promise<void>;
}

function CreateCustomerModal({ isOpen, onClose, onSubmit }: CreateCustomerModalProps) {
  const [formData, setFormData] = useState<Partial<CreateCustomerRequest>>({
    username: '',
    password: '',
    display_name: '',
    email: '',
    role: 'customer',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({ username: '', password: '', display_name: '', email: '', role: 'customer' });
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.username || formData.username.trim() === '') { setError('Username is required'); return; }
    if (!formData.password || formData.password.trim() === '') { setError('Password is required'); return; }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit({
        username: formData.username.trim(),
        password: formData.password,
        ...(formData.display_name && { display_name: formData.display_name.trim() }),
        ...(formData.email && { email: formData.email.trim() }),
        ...(formData.role && { role: formData.role }),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create customer');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="create-customer-modal">
      <div className="bg-card-bg border border-white/10 rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">Create Customer</h2>
          <button type="button" onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Username *</label>
            <input type="text" value={formData.username ?? ''} onChange={(e) => setFormData((prev) => ({ ...prev, username: e.target.value }))} className="input w-full" placeholder="e.g., jdoe" data-testid="customer-username-input" />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Password *</label>
            <input type="text" value={formData.password ?? ''} onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))} className="input w-full" placeholder="Enter password" data-testid="customer-password-input" />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Display Name</label>
            <input type="text" value={formData.display_name ?? ''} onChange={(e) => setFormData((prev) => ({ ...prev, display_name: e.target.value }))} className="input w-full" placeholder="e.g., John Doe" data-testid="customer-display-name-input" />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Email</label>
            <input type="text" value={formData.email ?? ''} onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))} className="input w-full" placeholder="e.g., jdoe@example.com" data-testid="customer-email-input" />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Role</label>
            <select value={formData.role ?? 'customer'} onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value as 'customer' | 'admin' }))} className="input w-full" data-testid="customer-role-select">
              <option value="customer">Customer</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 p-2 rounded border border-red-500">
              <strong>Error:</strong> {error}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={isSubmitting} className="btn btn-primary flex-1" data-testid="customer-submit-btn">
              {isSubmitting ? 'Creating...' : 'Create Customer'}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Imported legacy keys (masked) with admin Transfer / Release ─────

function keyStatusBadge(status: string): string {
  if (status === 'imported') return 'badge-success';
  if (status === 'unsupported_product') return 'badge-warning';
  if (status === 'revoked' || status === 'rejected' || status === 'owned_by_other_account') return 'badge-error';
  return 'badge-neutral';
}

function pidHex(pid: number | null): string {
  return pid === null ? '' : `0x${pid.toString(16).toUpperCase().padStart(4, '0')}`;
}

function ImportedKeysSection({ customer, customers }: { customer: Customer; customers: Customer[] }) {
  const [keys, setKeys] = useState<ImportedKey[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<{ keyId: number; kind: 'transfer' | 'release' } | null>(null);
  const [targetId, setTargetId] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setKeys((await apiClient.getImportedKeys(customer.id)).keys);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load imported keys');
    } finally {
      setIsLoading(false);
    }
  }, [customer.id]);

  useEffect(() => { setAction(null); setTargetId(''); load(); }, [load]);

  const confirm = async () => {
    if (!action) return;
    if (action.kind === 'transfer' && !targetId) { setError('Choose the customer to transfer to'); return; }
    setIsBusy(true);
    setError(null);
    try {
      if (action.kind === 'transfer') {
        await apiClient.transferImportedKey(action.keyId, Number(targetId));
      } else {
        await apiClient.releaseImportedKey(action.keyId);
      }
      setAction(null);
      setTargetId('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setIsBusy(false);
    }
  };

  const targets = customers.filter((c) => c.id !== customer.id && c.role === 'customer');
  const seatsLabel = (n: number | null) => (n === null ? '? seats' : n === 1 ? '1 seat' : `${n} seats`);

  return (
    <div className="pt-4 border-t border-white/10" data-testid="imported-keys-section">
      <p className="text-sm text-text-secondary mb-2">Imported License Keys</p>
      {isLoading && keys.length === 0 ? (
        <p className="text-xs text-dimmed">Loading...</p>
      ) : keys.length === 0 ? (
        <p className="text-xs text-dimmed" data-testid="imported-keys-empty">No imported keys</p>
      ) : (
        <div className="space-y-1 max-h-72 overflow-auto thin-scrollbar">
          {keys.map((k) => (
            <div key={k.id} className={clsx('rounded p-2 bg-dark-bg/60', k.key_type === 'SLIC' && 'ml-3')} data-testid={`imported-key-${k.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-mono text-text-primary truncate">{k.key_type} {k.key_masked}</span>
                <span className={clsx('badge', keyStatusBadge(k.status))} data-testid={`imported-key-status-${k.id}`}>{k.status}</span>
              </div>
              {k.key_type === 'SLIC' && (
                <p className="text-xs text-text-secondary truncate">
                  {k.product_name ?? 'Unknown product'} {pidHex(k.legacy_pid)} · {seatsLabel(k.seats)}
                  {k.sub_end ? ` · until ${k.sub_end}` : ' · permanent'}{k.expired ? ' (expired)' : ''}
                </p>
              )}
              {k.key_type === 'LICK' && (
                action?.keyId === k.id ? (
                  <div className="mt-2 space-y-2">
                    {action.kind === 'transfer' && (
                      <select value={targetId} onChange={(e) => setTargetId(e.target.value)} className="input w-full text-xs" aria-label={`Transfer ${k.key_masked} to customer`} data-testid={`transfer-target-${k.id}`}>
                        <option value="">Transfer to...</option>
                        {targets.map((c) => <option key={c.id} value={c.id}>{c.username}</option>)}
                      </select>
                    )}
                    <p className="text-xs text-text-secondary">
                      {action.kind === 'transfer'
                        ? 'Moves this license and its seats to the chosen customer.'
                        : 'Removes this license and its seats; it can then be imported again.'}
                    </p>
                    <div className="flex gap-2">
                      <button onClick={confirm} disabled={isBusy} className={clsx('btn text-xs', action.kind === 'release' ? 'btn-error' : 'btn-primary')} data-testid={`confirm-key-action-${k.id}`}>
                        {isBusy ? 'Working...' : action.kind === 'transfer' ? 'Confirm Transfer' : 'Confirm Release'}
                      </button>
                      <button onClick={() => { setAction(null); setTargetId(''); }} className="btn btn-secondary text-xs">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => setAction({ keyId: k.id, kind: 'transfer' })} className="btn btn-secondary text-xs" data-testid={`transfer-key-btn-${k.id}`}>Transfer</button>
                    <button onClick={() => setAction({ keyId: k.id, kind: 'release' })} className="btn btn-error text-xs" data-testid={`release-key-btn-${k.id}`}>Release</button>
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      )}
      {error && (
        <div className="mt-2 text-xs text-red-400 bg-red-500/10 p-2 rounded border border-red-500">
          <strong>Error:</strong> {error}
        </div>
      )}
    </div>
  );
}

interface CustomerDetailProps {
  customer: Customer;
  customers: Customer[];
  onClose: () => void;
  onUpdate: (id: number, request: UpdateCustomerRequest) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}

function CustomerDetail({ customer, customers, onClose, onUpdate, onDelete }: CustomerDetailProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editData, setEditData] = useState<UpdateCustomerRequest>({});
  const [editPassword, setEditPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsEditing(false);
    setShowDeleteConfirm(false);
    setError(null);
    setEditPassword('');
    setEditData({ display_name: customer.display_name ?? '', email: customer.email ?? '', role: customer.role, is_active: customer.is_active });
  }, [customer]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const request: UpdateCustomerRequest = { ...editData };
      if (editPassword.trim() !== '') { request.password = editPassword; }
      await onUpdate(customer.id, request);
      setIsEditing(false);
      setEditPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update customer');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await onDelete(customer.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete customer');
    }
  };

  return (
    <div data-testid="customer-detail-panel" className="card p-6 animate-slide-in-right">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-xs text-text-secondary mb-1">Customer Details</p>
          <h3 className="text-lg font-semibold text-text-primary">{customer.username}</h3>
        </div>
        <button type="button" onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary transition-colors" aria-label="Close" data-testid="close-detail-btn">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <div className="space-y-4">
        {!isEditing ? (
          <>
            <div className="flex items-center gap-4"><RoleBadge role={customer.role} /><StatusBadge isActive={customer.is_active} /></div>
            <div><p className="text-sm text-text-secondary mb-1">Display Name</p><p className="text-sm text-text-primary">{customer.display_name || '-'}</p></div>
            <div><p className="text-sm text-text-secondary mb-1">Email</p><p className="text-sm text-text-primary">{customer.email || '-'}</p></div>
            <div><p className="text-sm text-text-secondary mb-1">Created</p><p className="text-sm text-text-primary">{new Date(customer.created_at).toLocaleString()}</p></div>
          </>
        ) : (
          <>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Display Name</label>
              <input type="text" value={editData.display_name ?? ''} onChange={(e) => setEditData((prev) => ({ ...prev, display_name: e.target.value }))} className="input w-full" data-testid="customer-display-name-input" />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Email</label>
              <input type="text" value={editData.email ?? ''} onChange={(e) => setEditData((prev) => ({ ...prev, email: e.target.value }))} className="input w-full" data-testid="customer-email-input" />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Role</label>
              <select value={editData.role ?? customer.role} onChange={(e) => setEditData((prev) => ({ ...prev, role: e.target.value as 'customer' | 'admin' }))} className="input w-full" data-testid="customer-role-select">
                <option value="customer">Customer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="is-active" checked={editData.is_active ?? customer.is_active} onChange={(e) => setEditData((prev) => ({ ...prev, is_active: e.target.checked }))} className="w-4 h-4" />
              <label htmlFor="is-active" className="text-sm text-text-secondary">Active</label>
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">New Password (leave blank to keep current)</label>
              <input type="text" value={editPassword} onChange={(e) => setEditPassword(e.target.value)} className="input w-full" placeholder="Enter new password" data-testid="edit-password-input" />
            </div>
          </>
        )}

        {error && (
          <div className="text-sm text-red-400 bg-red-500/10 p-2 rounded border border-red-500">
            <strong>Error:</strong> {error}
          </div>
        )}

        <div className="flex gap-2 pt-4 border-t border-white/10">
          {!isEditing ? (
            <button onClick={() => setIsEditing(true)} className="btn btn-primary flex-1" data-testid="edit-customer-btn">Edit</button>
          ) : (
            <>
              <button onClick={handleSave} disabled={isSaving} className="btn btn-primary flex-1" data-testid="save-customer-btn">{isSaving ? 'Saving...' : 'Save'}</button>
              <button onClick={() => { setIsEditing(false); setEditPassword(''); setError(null); setEditData({ display_name: customer.display_name ?? '', email: customer.email ?? '', role: customer.role, is_active: customer.is_active }); }} className="btn btn-secondary">Cancel</button>
            </>
          )}

          {!showDeleteConfirm ? (
            <button onClick={() => setShowDeleteConfirm(true)} className="btn btn-error" data-testid="delete-customer-btn">Delete</button>
          ) : (
            <div className="flex gap-2">
              <button onClick={handleDelete} className="btn btn-error" data-testid="confirm-delete-btn">Confirm</button>
              <button onClick={() => setShowDeleteConfirm(false)} className="btn btn-secondary" data-testid="cancel-delete-btn">Cancel</button>
            </div>
          )}
        </div>

        {customer.role === 'customer' && <ImportedKeysSection customer={customer} customers={customers} />}
      </div>
    </div>
  );
}

export default function Customers() {
  const fetchCustomers = useStore((state) => state.fetchCustomers);
  const createCustomer = useStore((state) => state.createCustomer);
  const updateCustomer = useStore((state) => state.updateCustomer);
  const deleteCustomer = useStore((state) => state.deleteCustomer);
  const selectCustomer = useStore((state) => state.selectCustomer);
  const { customers, selectedCustomer, isLoading } = useCustomers();

  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleCreateCustomer = async (request: CreateCustomerRequest) => {
    await createCustomer(request);
  };

  const handleUpdateCustomer = async (id: number, request: UpdateCustomerRequest) => {
    const updated = await updateCustomer(id, request);
    selectCustomer(updated);
  };

  const handleDeleteCustomer = async (id: number) => {
    await deleteCustomer(id);
    selectCustomer(null);
  };

  return (
    <div className="h-full flex flex-col animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-text-primary">Customers</h1>
        <button onClick={() => setShowCreateModal(true)} className="btn btn-primary" data-testid="create-customer-btn">+ Create Customer</button>
      </div>

      <div className="flex-1 flex gap-6 min-h-0">
        <div className="flex-1 card overflow-hidden flex flex-col" data-testid="customer-list-table">
          <div className="grid grid-cols-[1fr_1fr_1fr_100px_100px_150px] gap-4 items-center p-3 bg-dark-bg border-b border-white/10 text-xs font-medium text-text-secondary uppercase tracking-wider">
            <span>Username</span><span>Display Name</span><span>Email</span><span>Role</span><span>Status</span><span>Created</span>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <svg className="h-8 w-8 animate-spin text-primary" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
            </div>
          ) : customers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <h3 className="mt-4 text-lg font-medium text-text-primary">No customers found</h3>
              <p className="mt-2 text-text-secondary">Create your first customer to get started</p>
              <button onClick={() => setShowCreateModal(true)} className="btn btn-primary mt-4">Create Customer</button>
            </div>
          ) : (
            <div className="flex-1 overflow-auto thin-scrollbar">
              {customers.map((customer) => (
                <div
                  key={customer.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => selectCustomer(customer)}
                  onKeyDown={(e) => e.key === 'Enter' && selectCustomer(customer)}
                  className={clsx(
                    'grid grid-cols-[1fr_1fr_1fr_100px_100px_150px] gap-4 items-center p-3 border-b border-white/5 cursor-pointer transition-colors',
                    selectedCustomer?.id === customer.id ? 'bg-primary/10' : 'hover:bg-light-bg'
                  )}
                  data-testid={`customer-row-${customer.username}`}
                >
                  <p className="text-sm font-medium text-text-primary truncate">{customer.username}</p>
                  <p className="text-sm text-text-secondary truncate">{customer.display_name || '-'}</p>
                  <p className="text-sm text-text-secondary truncate">{customer.email || '-'}</p>
                  <RoleBadge role={customer.role} />
                  <StatusBadge isActive={customer.is_active} />
                  <p className="text-xs text-text-secondary">{new Date(customer.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedCustomer && (
          <div className="w-96 shrink-0">
            <CustomerDetail key={selectedCustomer.id} customer={selectedCustomer} customers={customers} onClose={() => selectCustomer(null)} onUpdate={handleUpdateCustomer} onDelete={handleDeleteCustomer} />
          </div>
        )}
      </div>

      <CreateCustomerModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onSubmit={handleCreateCustomer} />
    </div>
  );
}
